import React, { useEffect } from 'react';
import { Space, Card, Select, Typography } from 'antd';
import { createUseStyles } from 'react-jss';
import type { SimulationDetail, SimulationFilterCountRecord } from '../../store/simulation/types';
import { useStoreActions, useStoreState } from '../../store';
import { useSimulationReport } from '../../hooks/simulation/useSimulationReport';
import { useSearchParams } from 'react-router-dom-v5-compat';
import { timeInTransitDataTypeMap } from './utils';
import { useCarriersFiltered } from '../../hooks/queries/useCarriersFiltered';

const useStyles = createUseStyles({
  filterCard: {
    marginBottom: '10px',
  },
  filterLabel: {
    marginRight: '5px',
  },
});

const { Title } = Typography;

export type SimulationReportFilterMap = Record<keyof Omit<SimulationFilterCountRecord, 'shipmentCount'>, Set<string>>;

export interface SimulationReportFiltersProps {
  simulation: SimulationDetail;
}
export const SimulationReportFilters = ({ simulation }: SimulationReportFiltersProps) => {
  const classes = useStyles();
  const [query, setQuery] = useSearchParams();
  const fetchTenants = useStoreActions((actions) => actions.tenant.fetchTenants);
  const tenantMap = useStoreState((state) => state.tenant.tenants);
  const carrierMap = useCarriersFiltered();

  const { reportData } = useSimulationReport(simulation, 'reporting-filters');

  useEffect(() => {
    fetchTenants({});
  }, [fetchTenants]);

  const filterCounts = reportData as SimulationFilterCountRecord[];

  if (!filterCounts) return null;

  // TODO: we have shipment counts so we can probably disable / cascade the filters at some point.
  // Going with the simplest implementation for now.
  const filterValues = filterCounts?.reduce<SimulationReportFilterMap>(
    (acc, row) => {
      if (!row) return acc;

      acc.originSlug.add(row.originSlug);
      if (row.tenantId) acc.tenantId.add(row.tenantId);
      acc.dayOfWeek.add(row.dayOfWeek);
      acc.carrier.add(row.carrier);
      acc.tntType.add(row.tntType);
      return acc;
    },
    {
      originSlug: new Set(),
      tenantId: new Set(),
      dayOfWeek: new Set(),
      carrier: new Set(),
      tntType: new Set(),
    },
  );

  // TODO: It would be nice to lookup a nicer origin name.

  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].filter((v) =>
    filterValues?.dayOfWeek.has(v),
  );

  return (
    <Card size={'small'} type={'inner'} className={classes.filterCard}>
      <Space size={'large'} wrap>
        <span>
          <Title className={classes.filterLabel} level={5}>
            Origin:
          </Title>
          <Select
            style={{ width: 150 }}
            value={query.get('originSlug') || 'all'}
            onChange={(value) => {
              if (!value || value === 'all') {
                query.delete('originSlug');
              } else {
                query.set('originSlug', value);
              }
              setQuery(query);
            }}
            options={[
              { label: 'All Origins', value: 'all' },
              ...Array.from(filterValues?.originSlug).map((v) => ({ label: v, value: v })),
            ]}
          />
        </span>
        {Object.values(tenantMap).length > 0 && (
          <span>
            <Title className={classes.filterLabel} level={5}>
              Tenant:
            </Title>
            <Select
              style={{ width: 180 }}
              value={query.get('tenantId') || 'all'}
              onChange={(value) => {
                if (!value || value === 'all') {
                  query.delete('tenantId');
                } else {
                  query.set('tenantId', value);
                }
                setQuery(query);
              }}
              options={[
                { label: 'All Tenants', value: 'all' },
                ...Array.from(filterValues?.tenantId).map((v) => ({ label: tenantMap[v]?.name, value: v })),
              ]}
            />
          </span>
        )}
        <span>
          <Title className={classes.filterLabel} level={5}>
            Carrier:
          </Title>
          <Select
            style={{ width: 300 }}
            value={query.get('carrier') || 'all'}
            onChange={(value) => {
              if (!value || value === 'all') {
                query.delete('carrier');
              } else {
                query.set('carrier', value);
              }
              setQuery(query);
            }}
            options={[
              { label: 'All Carriers', value: 'all' },
              ...Array.from(filterValues?.carrier).map((v) => ({ label: carrierMap[v]?.name, value: v })),
            ]}
          />
        </span>
        {filterValues?.tntType.size > 0 && (
          <span>
            <Title className={classes.filterLabel} level={5}>
              Time in Transit Source:
            </Title>
            <Select
              style={{ width: 220 }}
              value={query.get('tntType') || 'all'}
              onChange={(value) => {
                if (!value || value === 'all') {
                  query.delete('tntType');
                } else {
                  query.set('tntType', value);
                }
                setQuery(query);
              }}
              options={[
                { label: 'All Time in Transit Sources', value: 'all' },
                ...Array.from(filterValues?.tntType).map((v) => ({
                  label: timeInTransitDataTypeMap[v] || v,
                  value: v,
                })),
              ]}
            />
          </span>
        )}
        <span>
          <Title className={classes.filterLabel} level={5}>
            Day of Week:
          </Title>
          <Select
            style={{ width: 150 }}
            value={query.get('dayOfWeek') || 'all'}
            onChange={(value) => {
              if (value === 'all') {
                query.delete('dayOfWeek');
              } else {
                query.set('dayOfWeek', value);
              }
              setQuery(query);
            }}
            options={[
              { label: 'All Days', value: 'all' },
              ...Array.from(daysOfWeek).map((v) => ({ label: v, value: v })),
            ]}
          />
        </span>
      </Space>
    </Card>
  );
};
